import { Select as LayerSelect } from '@musinsa/layer-components-desktop'
import { useMemo, useRef, useState } from 'react'
import { createGlobalStyle } from 'styled-components'

/**
 * @musinsa/layer-components-desktop의 Select를 한 번 더 래핑한 공통 컴포넌트.
 * - 선택된 옵션에 배경 음영(highlight) 적용
 * - 드롭다운 열릴 때 첫 항목 자동 하이라이트 제거 (hover 시에만 표시)
 * - searchable 시 드롭다운 열리면 검색 입력창에 자동 포커스 (LOGISPRD-10836)
 */

const OPTION_LIST_CN = 'select-input__option-list'
const SELECTED_BG = 'var(--optionList-bg-optionItemSelected, rgba(0, 0, 0, 0.06))'
const HOVER_BG = 'var(--optionList-bg-optionItemHover, rgba(0, 0, 0, 0.06))'

const SelectStyle = createGlobalStyle`
  .${OPTION_LIST_CN} {
    padding: 6px;
  }
  .${OPTION_LIST_CN} ._1gcszyi0[data-highlighted] {
    background-color: transparent;
    outline: none;
  }
  .${OPTION_LIST_CN} ._1gcszyi0:hover {
    background-color: ${HOVER_BG};
  }
`

export interface SelectProps<T extends string> {
  value?: T
  onValueChange: (value: T) => void
  options: { value: T; label: string }[]
  placeholder?: string
  onClear?: () => void
  disabled?: boolean
  readOnly?: boolean
  floatingBlockLabel?: string
  required?: boolean
  validation?: 'none' | 'error'
  size?: 'medium' | 'small'
  optionMaxHeight?: string | number
  optionWidth?: string | number
  placement?: 'top' | 'bottom'
  /** 옵션 상단 검색 입력 노출(라벨 부분일치 필터). 항목이 많은 단일 select용 */
  searchable?: boolean
  searchPlaceholder?: string
}

export function Select<T extends string>({
  value,
  onValueChange,
  options,
  placeholder,
  onClear,
  disabled,
  readOnly,
  floatingBlockLabel,
  required,
  validation,
  size,
  optionMaxHeight,
  optionWidth,
  placement,
  searchable,
  searchPlaceholder,
}: SelectProps<T>) {
  const [term, setTerm] = useState('')
  // OptionInput 의 autoFocus 는 드롭다운 open 시점에 재실행되지 않아 먹지 않는다.
  // Trigger onClick(open 토글과 합성됨) 뒤 다음 프레임에 검색 input 을 직접 focus 한다(LOGISPRD-10836).
  const searchInputRef = useRef<HTMLInputElement>(null)
  // Radix Select.Item 은 빈 문자열 value 를 금지(런타임 throw)하므로, 빈 value 옵션을 원천 제거한다.
  const safeOptions = useMemo(() => options.filter((o) => `${o.value}` !== ''), [options])
  const filtered = useMemo(() => {
    if (!searchable || !term.trim()) return safeOptions
    const q = term.trim().toLowerCase()
    return safeOptions.filter((o) => o.label.toLowerCase().includes(q))
  }, [searchable, term, safeOptions])

  return (
    <>
      <SelectStyle />
      <LayerSelect.Root value={value} onValueChange={onValueChange} options={safeOptions}>
        <LayerSelect.Trigger
          placeholder={placeholder}
          onClear={onClear}
          floatingBlockLabel={floatingBlockLabel}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          validation={validation}
          size={size}
          onClick={searchable ? () => requestAnimationFrame(() => searchInputRef.current?.focus()) : undefined}
        />
        <LayerSelect.OptionList className={OPTION_LIST_CN} maxHeight={optionMaxHeight} width={optionWidth} placement={placement}>
          {searchable && (
            <LayerSelect.OptionInput
              ref={searchInputRef}
              value={term}
              onValueChange={setTerm}
              onClear={() => setTerm('')}
              placeholder={searchPlaceholder ?? '검색'}
            />
          )}
          {filtered.map((opt) => (
            <LayerSelect.Option
              key={opt.value}
              value={opt.value}
              label={opt.label}
              style={opt.value === value ? { backgroundColor: SELECTED_BG } : undefined}
            />
          ))}
          {searchable && filtered.length === 0 && <LayerSelect.OptionEmpty label="검색 결과가 없습니다." />}
        </LayerSelect.OptionList>
      </LayerSelect.Root>
    </>
  )
}

export default Select
